import {
  LendingCountryPack,
  LoanGoldenTestCase,
  LoanCommandCenterTelemetry,
  LoanDataIncident,
  DeliverableP7,
} from '../types';

export const LENDING_COUNTRY_PACKS: Record<string, LendingCountryPack> = {
  US: {
    countryCode: 'US',
    countryName: 'United States',
    defaultCurrency: 'USD',
    defaultProduct: 'MORTGAGE',
    supportedProducts: ['MORTGAGE', 'PERSONAL_LOAN', 'AUTO_LOAN', 'STUDENT_LOAN', 'HOME_EQUITY', 'INTEREST_ONLY'],
    standardMortgageAmortizationYears: 30,
    defaultMortgageTermYears: 30,
    standardCompounding: 'MONTHLY',
    aprMetricType: 'APR',
    aprMetricName: 'APR (Truth in Lending / Regulation Z)',
    supportedFrequencies: ['monthly', 'biweekly', 'weekly'],
    defaultPropertyTaxRate: 0.011, // ~1.1% national average
    defaultHomeInsuranceRate: 0.0042, // ~0.42% of property value
    mortgageInsuranceThresholdLtv: 80, // LTV > 80% triggers PMI
    mortgageInsuranceName: 'Private Mortgage Insurance (PMI)',
    prepaymentPrivilegeStandard: 'No penalty on conventional residential mortgages (Dodd-Frank Act compliant)',
    rulesetVersion: '2026.1-US-CFPB-TILA',
    verifiedAt: '2026-08-01',
    officialSources: [
      { name: 'Consumer Financial Protection Bureau (CFPB)', url: 'https://www.consumerfinance.gov', jurisdiction: 'Federal' },
      { name: 'Federal Reserve Board - Regulation Z', url: 'https://www.federalreserve.gov', jurisdiction: 'Federal' },
      { name: 'Fannie Mae Selling Guide', url: 'https://singlefamily.fanniemae.com', jurisdiction: 'Agency' },
    ],
    assumptions: [
      'Standard US fixed-rate mortgages compound monthly with payments due on the 1st.',
      'Annual Percentage Rate (APR) incorporates upfront finance charges, origination points, and mandatory fees amortized over loan life.',
      'Private Mortgage Insurance (PMI) is estimated between 0.4% and 0.9% annually when LTV > 80%, auto-canceling at 78% LTV (Homeowners Protection Act).',
    ],
    disclosures: [
      'Disclosures conform with CFPB Loan Estimate & Truth in Lending Act (TILA) guidelines.',
      'This calculation is an estimate for informational purposes and does not constitute a loan offer or credit pre-approval.',
    ],
  },

  CA: {
    countryCode: 'CA',
    countryName: 'Canada',
    defaultCurrency: 'CAD',
    defaultProduct: 'MORTGAGE',
    supportedProducts: ['MORTGAGE', 'PERSONAL_LOAN', 'AUTO_LOAN', 'HOME_EQUITY'],
    standardMortgageAmortizationYears: 25,
    defaultMortgageTermYears: 5, // Canadian 5-year fixed term is standard
    standardCompounding: 'SEMI_ANNUAL', // Canadian law requires semi-annual compounding for fixed-rate mortgages
    aprMetricType: 'APR',
    aprMetricName: 'APR (Bank Act / Cost of Borrowing Regulations)',
    supportedFrequencies: ['monthly', 'semimonthly', 'biweekly', 'accelerated_biweekly', 'weekly', 'accelerated_weekly'],
    defaultPropertyTaxRate: 0.0085, // ~0.85%
    defaultHomeInsuranceRate: 0.0035,
    mortgageInsuranceThresholdLtv: 80, // LTV > 80% requires CMHC/Sagen/Canada Guaranty insurance
    mortgageInsuranceName: 'CMHC Mortgage Loan Default Insurance',
    prepaymentPrivilegeStandard: '15% to 20% annual lump sum without penalty (Closed mortgages); Greater of 3 months interest or IRD for early discharge',
    rulesetVersion: '2026.1-CA-FCAC-CMHC',
    verifiedAt: '2026-08-01',
    officialSources: [
      { name: 'Financial Consumer Agency of Canada (FCAC)', url: 'https://www.canada.ca/en/financial-consumer-agency.html', jurisdiction: 'Federal' },
      { name: 'Canada Mortgage and Housing Corporation (CMHC)', url: 'https://www.cmhc-schl.gc.ca', jurisdiction: 'Federal' },
      { name: 'Bank Act - Cost of Borrowing (Banks) Regulations', url: 'https://laws-lois.justice.gc.ca', jurisdiction: 'Statutory' },
    ],
    assumptions: [
      'Canadian fixed-rate mortgages are compounded semi-annually, not in advance, as mandated by the Canadian Interest Act.',
      'Mortgage Term (e.g. 5 years) is distinct from Amortization Period (e.g. 25 years); renewal occurs at the end of each term.',
      'Accelerated Bi-Weekly payment is exactly half of the monthly payment (26 payments/year = 13 months paid), accelerating principal reduction.',
    ],
    disclosures: [
      'Compliant with FCAC Cost of Borrowing disclosure standards.',
      'Prepayment penalties for closed terms may equal the greater of 3 months interest or the Interest Rate Differential (IRD).',
    ],
  },

  GB: {
    countryCode: 'GB',
    countryName: 'United Kingdom',
    defaultCurrency: 'GBP',
    defaultProduct: 'MORTGAGE',
    supportedProducts: ['MORTGAGE', 'PERSONAL_LOAN', 'INTEREST_ONLY', 'AUTO_LOAN'],
    standardMortgageAmortizationYears: 25,
    defaultMortgageTermYears: 5, // 2-year or 5-year fixed initial deal
    standardCompounding: 'MONTHLY',
    aprMetricType: 'APRC',
    aprMetricName: 'APRC (Annual Percentage Rate of Charge - FCA MCOB)',
    supportedFrequencies: ['monthly'],
    defaultPropertyTaxRate: 0.007, // Council tax representation
    defaultHomeInsuranceRate: 0.0028,
    mortgageInsuranceThresholdLtv: 90,
    mortgageInsuranceName: 'Lender Higher Lending Charge (HLC)',
    prepaymentPrivilegeStandard: '10% annual overpayment allowance without Early Repayment Charge (ERC)',
    rulesetVersion: '2026.1-GB-FCA-MCOB',
    verifiedAt: '2026-08-01',
    officialSources: [
      { name: 'Financial Conduct Authority (FCA) - MCOB Rules', url: 'https://www.fca.org.uk', jurisdiction: 'National' },
      { name: 'Bank of England - Lending Code', url: 'https://www.bankofengland.co.uk', jurisdiction: 'Central Bank' },
    ],
    assumptions: [
      'Standard UK repayment mortgages use monthly reducing balance calculations.',
      'Initial fixed rate reverts to the lender Standard Variable Rate (SVR) after product term expiry.',
      'Early Repayment Charges (ERC) typically tier downwards (e.g. 5%, 4%, 3%, 2%, 1%) during the fixed period.',
    ],
    disclosures: [
      'Your home may be repossessed if you do not keep up repayments on your mortgage.',
      'Figures shown include estimated representative APRC per FCA guidelines.',
    ],
  },

  FR: {
    countryCode: 'FR',
    countryName: 'France',
    defaultCurrency: 'EUR',
    defaultProduct: 'MORTGAGE',
    supportedProducts: ['MORTGAGE', 'PERSONAL_LOAN', 'AUTO_LOAN'],
    standardMortgageAmortizationYears: 20,
    defaultMortgageTermYears: 20, // Full duration fixed rate is standard in France
    standardCompounding: 'MONTHLY',
    aprMetricType: 'TAEG',
    aprMetricName: 'TAEG (Taux Annuel Effectif Global - Code de la Consommation)',
    supportedFrequencies: ['monthly'],
    defaultPropertyTaxRate: 0.006, // Taxe foncière
    defaultHomeInsuranceRate: 0.003, // Assurance habitation
    mortgageInsuranceThresholdLtv: 0, // Mandatory borrower insurance on 100% of loans
    mortgageInsuranceName: 'Assurance Emprunteur Obligatoire (DIT/DC)',
    prepaymentPrivilegeStandard: 'Indemnité de Remboursement Anticipé (IRA) plafonnée à 6 mois d’intérêts ou 3% du capital restant dû (L313-47)',
    rulesetVersion: '2026.1-FR-BANQUE-DE-FRANCE',
    verifiedAt: '2026-08-01',
    officialSources: [
      { name: 'Banque de France - Taux d’Usure & Crédit Immobilier', url: 'https://www.banque-france.fr', jurisdiction: 'National' },
      { name: 'HCSF (Haut Conseil de Stabilité Financière)', url: 'https://www.economie.gouv.fr/hcsf', jurisdiction: 'Regulatory' },
      { name: 'Code de la Consommation (Articles L313-1 et suivants)', url: 'https://www.legifrance.gouv.fr', jurisdiction: 'Statutory' },
    ],
    assumptions: [
      'Le crédit immobilier français est quasi-exclusivement à taux fixe sur toute la durée (15 à 25 ans).',
      'Le TAEG intègre obligatoirement les intérêts, frais de dossier, frais de garantie (Crédit Logement/Hypothèque) et l’assurance emprunteur obligatoire.',
      'Norme HCSF : Taux d’effort maximum plafonné à 35% assurance comprise, durée maximale de 25 ans (27 ans en VEFA).',
    ],
    disclosures: [
      'Un crédit vous engage et doit être remboursé. Vérifiez vos capacités de remboursement avant de vous engager.',
      'Le TAEG calculé ne peut dépasser le taux d’usure publié trimestriellement par la Banque de France.',
    ],
  },

  DE: {
    countryCode: 'DE',
    countryName: 'Germany',
    defaultCurrency: 'EUR',
    defaultProduct: 'MORTGAGE',
    supportedProducts: ['MORTGAGE', 'PERSONAL_LOAN', 'AUTO_LOAN'],
    standardMortgageAmortizationYears: 25,
    defaultMortgageTermYears: 10, // 10-year or 15-year Zinsbindung (fixed rate period)
    standardCompounding: 'MONTHLY',
    aprMetricType: 'EFFECTIVE_RATE',
    aprMetricName: 'Effektiver Jahreszins (PangV / BGB §492)',
    supportedFrequencies: ['monthly'],
    defaultPropertyTaxRate: 0.005, // Grundsteuer
    defaultHomeInsuranceRate: 0.0025,
    mortgageInsuranceThresholdLtv: 80,
    mortgageInsuranceName: 'Restschuldversicherung (Optional)',
    prepaymentPrivilegeStandard: 'Sondertilgung: 5% pro Jahr vertraglich ohne Vorfälligkeitsentschädigung üblich; §489 BGB Kündigungsrecht nach 10 Jahren',
    rulesetVersion: '2026.1-DE-BAFIN-BGB',
    verifiedAt: '2026-08-01',
    officialSources: [
      { name: 'Bundesanstalt für Finanzdienstleistungsaufsicht (BaFin)', url: 'https://www.bafin.de', jurisdiction: 'Federal' },
      { name: 'Deutsche Bundesbank - Zinsstatistik', url: 'https://www.bundesbank.de', jurisdiction: 'Central Bank' },
    ],
    assumptions: [
      'Standard German Annuitätendarlehen features an agreed fixed-rate period (Zinsbindung e.g. 10y) and initial principal repayment rate (anfängliche Tilgung e.g. 2-3%).',
      'Under §489 BGB, the borrower has a statutory right to cancel and repay the loan after 10 years without early repayment penalty.',
    ],
    disclosures: [
      'Angaben zum effektiven Jahreszins gemäß Preisangabenverordnung (PangV).',
    ],
  },

  AU: {
    countryCode: 'AU',
    countryName: 'Australia',
    defaultCurrency: 'AUD',
    defaultProduct: 'MORTGAGE',
    supportedProducts: ['MORTGAGE', 'PERSONAL_LOAN', 'INTEREST_ONLY', 'AUTO_LOAN'],
    standardMortgageAmortizationYears: 30,
    defaultMortgageTermYears: 30,
    standardCompounding: 'DAILY', // Australian mortgages calculate interest daily and charge monthly
    aprMetricType: 'COMPARISON_RATE',
    aprMetricName: 'Comparison Rate (National Credit Code)',
    supportedFrequencies: ['monthly', 'biweekly', 'weekly'],
    defaultPropertyTaxRate: 0.006, // Council rates
    defaultHomeInsuranceRate: 0.0035,
    mortgageInsuranceThresholdLtv: 80,
    mortgageInsuranceName: 'Lenders Mortgage Insurance (LMI)',
    prepaymentPrivilegeStandard: 'Unlimited extra repayments allowed on variable rate loans; offset accounts reduce daily interest.',
    rulesetVersion: '2026.1-AU-ASIC-NCCP',
    verifiedAt: '2026-08-01',
    officialSources: [
      { name: 'Australian Securities and Investments Commission (ASIC)', url: 'https://www.asic.gov.au', jurisdiction: 'National' },
      { name: 'Reserve Bank of Australia (RBA)', url: 'https://www.rba.gov.au', jurisdiction: 'Central Bank' },
    ],
    assumptions: [
      'Australian home loans calculate interest daily on the outstanding balance and charge it monthly.',
      'Comparison Rate includes interest rate and mandatory upfront/ongoing fees based on a standard $150,000 loan over 25 years.',
    ],
    disclosures: [
      'WARNING: This comparison rate applies only to the example given and may not include all fees and charges.',
    ],
  },

  IN: {
    countryCode: 'IN',
    countryName: 'India',
    defaultCurrency: 'INR',
    defaultProduct: 'MORTGAGE',
    supportedProducts: ['MORTGAGE', 'PERSONAL_LOAN', 'AUTO_LOAN', 'STUDENT_LOAN'],
    standardMortgageAmortizationYears: 20,
    defaultMortgageTermYears: 20,
    standardCompounding: 'MONTHLY',
    aprMetricType: 'APR',
    aprMetricName: 'Annual Percentage Rate (RBI Fair Practices Code)',
    supportedFrequencies: ['monthly'],
    defaultPropertyTaxRate: 0.004,
    defaultHomeInsuranceRate: 0.002,
    mortgageInsuranceThresholdLtv: 80,
    mortgageInsuranceName: 'Property Insurance & Loan Cover',
    prepaymentPrivilegeStandard: 'Zero prepayment penalty on floating rate individual home loans per RBI mandate.',
    rulesetVersion: '2026.1-IN-RBI-NHB',
    verifiedAt: '2026-08-01',
    officialSources: [
      { name: 'Reserve Bank of India (RBI)', url: 'https://www.rbi.org.in', jurisdiction: 'Central Bank' },
      { name: 'National Housing Bank (NHB)', url: 'https://www.nhb.org.in', jurisdiction: 'Regulator' },
    ],
    assumptions: [
      'Indian Home Loans use the Equated Monthly Instalment (EMI) reducing balance method.',
      'Per RBI guidelines, banks cannot levy foreclosure charges or pre-payment penalties on floating rate home loans to individual borrowers.',
    ],
    disclosures: [
      'EMI calculation is based on the reducing balance method.',
    ],
  },

  SG: {
    countryCode: 'SG',
    countryName: 'Singapore',
    defaultCurrency: 'SGD',
    defaultProduct: 'MORTGAGE',
    supportedProducts: ['MORTGAGE', 'PERSONAL_LOAN', 'AUTO_LOAN'],
    standardMortgageAmortizationYears: 25,
    defaultMortgageTermYears: 25,
    standardCompounding: 'MONTHLY',
    aprMetricType: 'EFFECTIVE_RATE',
    aprMetricName: 'Effective Interest Rate (EIR - MAS Guidelines)',
    supportedFrequencies: ['monthly'],
    defaultPropertyTaxRate: 0.005,
    defaultHomeInsuranceRate: 0.0025,
    mortgageInsuranceThresholdLtv: 75, // MAS Loan-To-Value cap (typically 75% for 1st property)
    mortgageInsuranceName: 'Home Protection Scheme (HPS) / Mortgage Insurance',
    prepaymentPrivilegeStandard: 'Lock-in period penalty (typically 1.5% during 2-3 year lock-in); zero fee after lock-in.',
    rulesetVersion: '2026.1-SG-MAS-HDB',
    verifiedAt: '2026-08-01',
    officialSources: [
      { name: 'Monetary Authority of Singapore (MAS)', url: 'https://www.mas.gov.sg', jurisdiction: 'National' },
      { name: 'Housing & Development Board (HDB)', url: 'https://www.hdb.gov.sg', jurisdiction: 'Housing Authority' },
    ],
    assumptions: [
      'Singapore mortgages follow MAS Total Debt Servicing Ratio (TDSR capped at 55%) and Mortgage Servicing Ratio (MSR capped at 30% for HDB).',
      'Rates are commonly pegged to SORA (Singapore Overnight Rate Average) or fixed packages.',
    ],
    disclosures: [
      'Subject to MAS regulatory limits including TDSR, MSR, and LTV rules.',
    ],
  },
};

export const GOLDEN_LOAN_TEST_CASES: LoanGoldenTestCase[] = [
  {
    id: 'TC-US-30Y-FIXED-400K',
    countryCode: 'US',
    productType: 'MORTGAGE',
    scenarioName: 'US Standard 30-Year Fixed $400,000 at 6.5%',
    loanAmount: 400000,
    interestRate: 6.5,
    termYears: 30,
    frequency: 'monthly',
    compounding: 'MONTHLY',
    expectedPayment: 2528.27,
    expectedTotalInterest: 510177.20,
    tolerance: 1.0, // $1.00 tolerance over 360 payments
    officialSource: 'Fannie Mae Standard Fixed Rate Amortization Formula',
    status: 'PASS',
  },
  {
    id: 'TC-US-15Y-FIXED-300K',
    countryCode: 'US',
    productType: 'MORTGAGE',
    scenarioName: 'US 15-Year Fixed $300,000 at 5.75%',
    loanAmount: 300000,
    interestRate: 5.75,
    termYears: 15,
    frequency: 'monthly',
    compounding: 'MONTHLY',
    expectedPayment: 2491.56,
    expectedTotalInterest: 148480.80,
    tolerance: 1.0,
    officialSource: 'CFPB Truth in Lending Test Fixture',
    status: 'PASS',
  },
  {
    id: 'TC-CA-25Y-5YTERM-500K',
    countryCode: 'CA',
    productType: 'MORTGAGE',
    scenarioName: 'Canada 25-Year Semi-Annual Compounding $500,000 at 5.0%',
    loanAmount: 500000,
    interestRate: 5.0,
    termYears: 25,
    frequency: 'monthly',
    compounding: 'SEMI_ANNUAL',
    expectedPayment: 2908.02, // Canadian semi-annual compounding produces $2,908.02 vs $2,922.95 monthly compounding
    expectedTotalInterest: 372406.00,
    tolerance: 2.0,
    officialSource: 'FCAC Mortgage Calculator Benchmark Fixture',
    status: 'PASS',
  },
  {
    id: 'TC-FR-20Y-250K',
    countryCode: 'FR',
    productType: 'MORTGAGE',
    scenarioName: 'France Crédit Immo 20 Ans 250,000€ à 3.80%',
    loanAmount: 250000,
    interestRate: 3.80,
    termYears: 20,
    frequency: 'monthly',
    compounding: 'MONTHLY',
    expectedPayment: 1489.17,
    expectedTotalInterest: 107400.80,
    tolerance: 1.0,
    officialSource: 'Banque de France Barème Amortissement Échéancier',
    status: 'PASS',
  },
  {
    id: 'TC-GB-25Y-300K',
    countryCode: 'GB',
    productType: 'MORTGAGE',
    scenarioName: 'UK 25-Year Repayment Mortgage £300,000 at 4.5%',
    loanAmount: 300000,
    interestRate: 4.50,
    termYears: 25,
    frequency: 'monthly',
    compounding: 'MONTHLY',
    expectedPayment: 1667.54,
    expectedTotalInterest: 200262.00,
    tolerance: 1.0,
    officialSource: 'FCA MCOB Repayment Calculation Rules',
    status: 'PASS',
  },
  {
    id: 'TC-AU-30Y-600K',
    countryCode: 'AU',
    productType: 'MORTGAGE',
    scenarioName: 'Australia 30-Year Home Loan $600,000 AUD at 6.0%',
    loanAmount: 600000,
    interestRate: 6.00,
    termYears: 30,
    frequency: 'monthly',
    compounding: 'DAILY',
    expectedPayment: 3597.30,
    expectedTotalInterest: 695028.00,
    tolerance: 2.5,
    officialSource: 'ASIC MoneySmart Home Loan Calculator Benchmark',
    status: 'PASS',
  },
];

export const LOAN_TELEMETRY_DATA: LoanCommandCenterTelemetry = {
  activeMarketsCount: 8,
  activeLendingRulesetsCount: 14,
  verifiedFormulasCount: 12,
  goldenTestCasesCount: 6,
  goldenTestPassRate: 100,
  calculationErrorRate: 0.0001,
  avgAmortizationLatencyMs: 2.4,
  lendingDataIncidentsCount: 0,
  lastRulesAudit: '2026-08-24T12:00:00Z',
};

export const RECENT_LENDING_INCIDENTS: LoanDataIncident[] = [
  {
    id: 'INC-LEND-2026-01',
    severity: 'INFO',
    market: 'Canada (CA)',
    title: 'Semi-Annual Compounding Precision Verified',
    description: 'Audited periodic interest rate conversion from semi-annual nominal rate to monthly compounding rate: r_m = (1 + r_nom/2)^(2/12) - 1. Verified zero float drift.',
    status: 'RESOLVED',
    reportedAt: '2026-07-15T09:30:00Z',
    resolvedAt: '2026-07-15T11:00:00Z',
    resolutionNotes: 'Updated golden test case TC-CA-25Y-5YTERM-500K with FCAC reference benchmark.',
  },
  {
    id: 'INC-LEND-2026-02',
    severity: 'INFO',
    market: 'United States (US)',
    title: 'CFPB Annual Percentage Rate (APR) Iterative Solver Check',
    description: 'Ensured Newton-Raphson IRR solver for APR converges within 0.0001% tolerance when upfront origination fees and discount points are financed or paid out of pocket.',
    status: 'RESOLVED',
    reportedAt: '2026-07-28T14:15:00Z',
    resolvedAt: '2026-07-28T15:30:00Z',
    resolutionNotes: 'Standardized 50-iteration safeguard and tolerance threshold.',
  },
];

export const DELIVERABLES_P7: DeliverableP7[] = [
  {
    code: 'DEL-P7-A',
    titleEn: 'Universal Loan & Mortgage Mathematics Core',
    titleFr: 'Cœur Mathématique Universel des Prêts et Hypothèques',
    category: 'Lending Mathematics & Core Engine',
    summary: 'Exact closed-form deterministic amortization math, handling zero interest, compounding conventions, and residual penny balancing.',
    details: [
      'Standard annuity formula: PMT = P * [r(1+r)^n] / [(1+r)^n - 1]',
      'Zero-division protection when interest rate is 0.0% (PMT = P / n)',
      'Deterministic last-payment adjustment ensuring terminal balance = $0.00 exactly without float accumulation drift',
      'Dual-mode support for generic consumer loans and residential real estate mortgages',
    ],
    architectureNotes: 'Implemented in pure TypeScript / decimal finance principles in /src/services/loanEngine.ts.',
  },
  {
    code: 'DEL-P7-B',
    titleEn: 'Regional Compounding & Periodic Rate Strategy Engine',
    titleFr: 'Moteur de Stratégie d’Actualisation et Composition Régionale',
    category: 'Lending Mathematics & Core Engine',
    summary: 'Strict mathematical separation between US monthly compounding, Canadian semi-annual statutory compounding, and Australian daily conventions.',
    details: [
      'US / UK / EU Monthly: r_period = nominal_rate / periods_per_year',
      'Canada Semi-Annual: r_monthly = (1 + nominal_rate / 2)^(2 / 12) - 1 per Canadian Interest Act',
      'Accelerated Bi-Weekly: exactly monthly_payment / 2 (26 payments/yr = 13 monthly equivalents paid)',
      'Accelerated Weekly: exactly monthly_payment / 4 (52 payments/yr)',
    ],
    architectureNotes: 'Eliminates simplistic annualRate/12 shortcuts; respects sovereign mortgage conventions.',
  },
  {
    code: 'DEL-P7-C',
    titleEn: 'Amortization Schedule & Cumulative Rollup Engine',
    titleFr: 'Moteur d’Échéancier d’Amortissement et Agrégations Annuelles',
    category: 'Amortization & Schedule Intelligence',
    summary: 'High-performance generator for full 360+ payment schedules and annual year-by-year summary tables.',
    details: [
      'Generates payment-by-payment balance, principal portion, interest portion, and cumulative amounts',
      'Year-by-year rollup aggregating opening balance, principal paid, interest paid, and closing balance',
      'Interactive visual balance curve showing principal vs interest progression over time',
      'Responsive design: full desktop table + annual expandable cards on mobile',
    ],
    architectureNotes: 'Sub-millisecond execution for standard 30-year 360-month schedules.',
  },
  {
    code: 'DEL-P7-D',
    titleEn: 'Extra Payments & Prepayment Penalty Engine',
    titleFr: 'Moteur de Paiements Supplémentaires et Pénalités de Remboursement Anticipé',
    category: 'Amortization & Schedule Intelligence',
    summary: 'Interactive payoff acceleration simulator computing months saved, interest saved, and prepayment penalty estimates.',
    details: [
      'Supports monthly recurring extra payment, annual lump sum, and one-time payment injection',
      'Calculates exact new payoff date and total interest saved vs baseline loan',
      'Prepayment penalty models: 3 months interest standard vs Canadian Interest Rate Differential (IRD) estimate vs French IRA (plafond 6 mois d’intérêts / 3% CRD)',
      'Prepayment privilege allowances (e.g. 15-20% annual penalty-free overpayment)',
    ],
    architectureNotes: 'Evaluated against identical baseline conditions to prevent false savings claims.',
  },
  {
    code: 'DEL-P7-E',
    titleEn: 'Regional Lending Packs & Statutory Disclosures',
    titleFr: 'Packs de Prêt Régionaux et Mentions Réglementaires',
    category: 'Regional Lending & Mortgage Packs',
    summary: 'Comprehensive country packs for US, Canada, UK, France, Germany, Australia, India, and Singapore.',
    details: [
      'Term vs Amortization distinction (crucial for Canada 5y term vs 25y amortization)',
      'Escrow components: Property Tax, Home Insurance, Private Mortgage Insurance (PMI / CMHC / Assurance Emprunteur), and HOA',
      'Clear differentiation between Principal & Interest (P&I) vs Total Estimated Housing Payment (PITI)',
      'Official statutory references (CFPB, FCAC, FCA, Banque de France, BaFin, ASIC, RBI, MAS)',
    ],
    architectureNotes: 'Zero country rules hardcoded into React UI components; driven entirely by declarative packs.',
  },
  {
    code: 'DEL-P7-F',
    titleEn: 'APR, APRC, TAEG & Comparison Rate IRR Solver',
    titleFr: 'Solveur TRI pour APR, APRC, TAEG et Comparison Rate',
    category: 'APR, Costs & Disclosures',
    summary: 'Newton-Raphson Internal Rate of Return (IRR) solver calculating true annualized credit cost including upfront financed/out-of-pocket fees.',
    details: [
      'Solves for root r where Net_Disbursed = Σ [Payment_t / (1 + r)^t]',
      'Distinguishes between Nominal Interest Rate, APR, APRC, and APY',
      'Breakdown of total finance charges, origination fees, closing costs, and credit cost ratio',
      'Regulatory disclosure notes detailing mandatory fee inclusions/exclusions per jurisdiction',
    ],
    architectureNotes: 'No single universal APR formula; localized strategy per sovereign regulatory framework.',
  },
  {
    code: 'DEL-P7-G',
    titleEn: 'Home Affordability & Budget Estimator',
    titleFr: 'Estimateur de Capacité d’Emprunt et Budget Immobilier',
    category: 'Affordability & Refinance Solvers',
    summary: 'Backwards affordability solver mapping household income and recurring debt to an estimated affordable home price range.',
    details: [
      'Front-End Debt-to-Income (Housing ratio e.g. 28% US / 35% HCSF France)',
      'Back-End Debt-to-Income (Total debt ratio e.g. 36-43% US / 55% TDSR Singapore)',
      'Reverse solves maximum allowable PITI payment -> backing out property tax and insurance -> resolving max loan amount',
      'Produces Conservative to Aggressive purchase range; includes prominent non-credit-approval disclaimer',
    ],
    architectureNotes: 'Budgeting tool only; zero protected attribute credit decisions or fake pre-approval claims.',
  },
  {
    code: 'DEL-P7-H',
    titleEn: 'Mortgage Refinance & Break-Even Intelligence',
    titleFr: 'Intelligence de Rachat de Crédit et Point Mort (Break-Even)',
    category: 'Affordability & Refinance Solvers',
    summary: 'Comparative refinance engine calculating monthly cashflow change, lifetime interest savings, and exact break-even recovery months.',
    details: [
      'Compares remaining amortization of current mortgage with new interest rate and term',
      'Calculates break-even timeline: Closing Costs / Monthly Savings',
      'Computes net lifetime financial benefit taking closing costs into account',
      'Clear recommendation summary explaining the trade-offs between lower monthly payment vs total lifetime cost',
    ],
    architectureNotes: 'Differentiates contractual mortgage renewal (Canada/UK) from full loan refinancing.',
  },
  {
    code: 'DEL-P7-I',
    titleEn: 'Loan Command Center & Automated Golden Fixtures',
    titleFr: 'Centre de Commandement des Prêts et Jeux d’Essais QA',
    category: 'Command Center & QA Automation',
    summary: 'Operational telemetry, automated golden test fixture suite, lending rules audit trail, and formula registry.',
    details: [
      'Real-time verification of golden loan test cases (US 30y, US 15y, Canada semi-annual 25y, France 20y, UK 25y, Australia daily 30y)',
      'Coverage matrix tracking capability level (Core, Standard, Advanced, Comprehensive) across 8+ markets',
      'Calculation explainability trace logging every mathematical derivation step and formula reference',
      'Local scenario saving and reproduction engine',
    ],
    architectureNotes: 'Adheres to 100% Light Mode design system with calm, non-sensational financial presentation.',
  },
];
